/**
 * Unified Error Handling System
 * Provides consistent error handling across the application with improved type safety
 */

import { isHttpError, isNetworkError, isObject, isString } from "./typeGuards"

export interface AppError {
  message: string
  code?: string
  status?: number
  details?: Record<string, unknown>
  timestamp: string
}

export enum ErrorType {
  NETWORK = "NETWORK_ERROR",
  VALIDATION = "VALIDATION_ERROR",
  AUTHENTICATION = "AUTH_ERROR",
  AUTHORIZATION = "PERMISSION_ERROR",
  NOT_FOUND = "NOT_FOUND",
  SERVER = "SERVER_ERROR",
  UNKNOWN = "UNKNOWN_ERROR",
}

export class AppErrorClass extends Error {
  public readonly code: string
  public readonly status?: number
  public readonly details?: Record<string, unknown>
  public readonly timestamp: string

  constructor(
    message: string,
    code: string = ErrorType.UNKNOWN,
    status?: number,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = "AppError"
    this.code = code
    this.status = status
    this.details = details
    this.timestamp = new Date().toISOString()

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppErrorClass)
    }
  }

  toJSON(): AppError {
    return {
      message: this.message,
      code: this.code,
      status: this.status,
      details: this.details,
      timestamp: this.timestamp,
    }
  }
}

/**
 * Create a standardized error from various input types
 */
export const createAppError = (
  error: unknown,
  defaultMessage = "An unexpected error occurred",
): AppErrorClass => {
  // If it's already an AppError, return as is
  if (error instanceof AppErrorClass) {
    return error
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return new AppErrorClass(error.message || defaultMessage)
  }

  // Handle HTTP response errors using type guard
  if (isHttpError(error)) {
    const status = error.response.status
    const responseData = error.response.data

    let message = defaultMessage
    let responseCode: string | undefined

    // Extract message from various response formats using type guards
    if (isObject(responseData)) {
      if (isString(responseData.message)) {
        message = responseData.message
      } else if (
        isObject(responseData.error) &&
        isString(responseData.error.message)
      ) {
        message = responseData.error.message
      } else if (isString(responseData.detail)) {
        message = responseData.detail
      }

      if (isString((responseData as { code?: unknown }).code)) {
        responseCode = (responseData as { code: string }).code
      }
    }

    let code: string | undefined = responseCode

    // Determine error type based on status
    if (!code) {
      if (status === 400) {
        code = ErrorType.VALIDATION
      } else if (status === 401) {
        code = ErrorType.AUTHENTICATION
      } else if (status === 403) {
        code = ErrorType.AUTHORIZATION
      } else if (status === 404) {
        code = ErrorType.NOT_FOUND
      } else if (status >= 500) {
        code = ErrorType.SERVER
      } else {
        code = ErrorType.UNKNOWN
      }
    }

    return new AppErrorClass(
      message,
      code,
      status,
      isObject(responseData) ? responseData : undefined,
    )
  }

  // Handle network errors using type guard
  if (isNetworkError(error)) {
    return new AppErrorClass(
      "Network connection failed. Please check your internet connection.",
      ErrorType.NETWORK,
    )
  }

  // Handle objects with message property
  if (isObject(error)) {
    const messageValue = error.message
    if (isString(messageValue)) {
      return new AppErrorClass(messageValue)
    }
  }

  // Handle string errors
  if (typeof error === "string") {
    return new AppErrorClass(error)
  }

  // Fallback for unknown error types
  return new AppErrorClass(defaultMessage)
}

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: unknown): string => {
  const appError = createAppError(error)
  return appError.message
}

/**
 * Check if error should be retried automatically
 */
export const isRetryableError = (error: AppErrorClass): boolean => {
  const retryableCodes = [ErrorType.NETWORK, ErrorType.SERVER]

  const retryableStatuses = [500, 502, 503, 504]

  return (
    retryableCodes.includes(error.code as ErrorType) ||
    (error.status !== undefined && retryableStatuses.includes(error.status))
  )
}

/**
 * Check if error indicates user should re-authenticate
 */
export const isAuthError = (error: AppErrorClass): boolean => {
  return error.code === ErrorType.AUTHENTICATION || error.status === 401
}

/**
 * Log error to console with structured format
 */
export const logError = (
  error: AppErrorClass,
  context?: string,
  meta: Record<string, unknown> = {},
): void => {
  const hasWindow = typeof window !== "undefined"
  const hasNavigator = typeof navigator !== "undefined"

  const errorInfo: Record<string, unknown> = {
    context: context || "Unknown",
    error: error.toJSON(),
    stack: error.stack,
    ...meta,
  }

  if (hasNavigator) {
    errorInfo.userAgent = navigator.userAgent
  }

  if (hasWindow) {
    errorInfo.url = window.location.href
  }

  if (!errorInfo.requestId && error.details && isObject(error.details)) {
    const details = error.details as Record<string, unknown>
    const requestId =
      (typeof details.request_id === "string" && details.request_id) ||
      (typeof details.requestId === "string" && details.requestId)
    if (requestId) {
      errorInfo.requestId = requestId
    }
  }

  console.error("Application Error:", errorInfo)

  // In production, you might want to send this to an error tracking service
  // e.g., Sentry, LogRocket, etc.
}

/**
 * Default error boundary fallback
 */
export const getErrorBoundaryMessage = (error: Error): string => {
  if (error instanceof AppErrorClass) {
    return error.message
  }

  return "Something went wrong. Please refresh the page and try again."
}

/**
 * Legacy handleError function - creates an AppError from ApiError
 * Note: This is a compatibility function. For new code, use createAppError directly
 * and handle error display in components using the appropriate toast hook.
 */
export const handleError = (err: any): AppErrorClass => {
  const errDetail = err?.body?.detail || err?.detail
  let errorMessage = errDetail || "Something went wrong."

  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg || errDetail[0].message || errDetail[0]
  }

  return createAppError(err, errorMessage)
}
